import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export interface CompanyListItem {
  id: number;
  name: string;
  external_ref: string;
  profile_url?: string | null;
  created?: string | null;
  updated?: string | null;
}

export interface PaginatedCompaniesResponse {
  items: CompanyListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface CompanyIndustry {
  id: number;
  name: string;
  external_ref: string;
}

export interface CompanyLocation {
  country?: string | null;
  geographic_area?: string | null;
  city?: string | null;
  postal_code?: string | null;
  line1?: string | null;
  line2?: string | null;
  is_headquarters?: boolean;
}

export interface FundingRoundInvestor {
  name: string;
  crunchbase_url?: string | null;
}

export interface FundingRound {
  id: number;
  funding_type?: string | null;
  money_raised_amount?: number | null;
  money_raised_currency?: string | null;
  announced_on?: string | null;
  num_other_investors?: number | null;
  funding_round_crunchbase_url?: string | null;
  investors: FundingRoundInvestor[];
}

export interface CompanyDetail extends CompanyListItem {
  industry?: string | null;
  company_type?: string | null;
  company_type_source?: string | null;
  employee_count_range?: string | null;
  staff_count?: number | null;
  staff_count_range_start?: number | null;
  staff_count_range_end?: number | null;
  specialties?: string[] | null;
  headquarters?: string | null;
  founded_year?: number | null;
  follower_count?: number | null;
  website_url?: string | null;
  description?: string | null;
  industries?: CompanyIndustry[] | null;
  locations?: CompanyLocation[] | null;
  crunchbase_url?: string | null;
  num_funding_rounds?: number | null;
  funding_rounds?: FundingRound[] | null;
}

export interface CompanyJobPosting {
  id: number;
  title: string;
  location?: string | null;
  external_url?: string | null;
  posted_at?: string | null;
  is_active: boolean;
  fetched_at?: string | null;
}

export interface CompanyJobPostingsResponse {
  company_id: number;
  total: number;
  page: number;
  size: number;
  active_count: number;
  mass_hiring: boolean;
  mass_hiring_threshold: number;
  job_postings: CompanyJobPosting[];
}

export async function getCompanyDetail(navigate, companyId: number) {
  const url = `${API_URL}/companies/${companyId}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement de l'entreprise");
  return res.json() as Promise<CompanyDetail>;
}

export async function enqueueCompanyEnrichment(navigate, companyId: number) {
  const url = `${API_URL}/companies/${companyId}/enqueue-enrichment`;
  const res = await apiFetch(url, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la mise en file d'enrichissement");
  return res.json() as Promise<{ status: string; company_id: number }>;
}

export async function enqueueCompanyJobsScraping(navigate, companyId: number) {
  const url = `${API_URL}/companies/${companyId}/enqueue-jobs-scraping`;
  const res = await apiFetch(url, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la mise en file de scraping des offres");
  return res.json() as Promise<{ status: string; company_id: number }>;
}

export async function getCompanyJobPostings(
  navigate,
  companyId: number,
  params?: { active_only?: boolean; page?: number; size?: number }
) {
  const q = new URLSearchParams();
  q.set("active_only", String(params?.active_only ?? true));
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 50));
  const url = `${API_URL}/companies/${companyId}/job-postings?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des offres d'emploi");
  return res.json() as Promise<CompanyJobPostingsResponse>;
}

export async function listCompanies(
  navigate: NavigateFunction,
  params?: { page?: number; size?: number; sort_by?: string; sort_order?: string }
) {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  if (params?.sort_by) q.set("sort_by", params.sort_by);
  if (params?.sort_order) q.set("sort_order", params.sort_order);
  const url = `${API_URL}/companies?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des entreprises");
  return res.json() as Promise<PaginatedCompaniesResponse>;
}
